import { Component, EventEmitter, Input, Output, effect, inject, input, signal, viewChild } from '@angular/core';
import { AppVersion, AppVersionStatuses } from '@zerops/models/app-version';
import { ProcessStatuses } from '@zerops/models/process';
import { ZefReactiveComponent, distinctUntilKeysChanged } from '@zerops/zef/core';
import { ServiceStackEntity } from '@zerops/zerops/core/service-stack-base';
import { BuildProcessStates, BuildProcessStepState, getPipelineState } from '@zerops/zui/build-state-steps';
import { ObservableInput } from 'observable-input';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  scan,
  startWith,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { FEATURE_NAME } from './pipeline-detail.constant';
import { copyCodeDialogOpen } from '../copy-code-dialog';
import { buildCancel, ProcessEntity } from '@zerops/zerops/core/process-base';
import { PipelineDetailModes } from './pipeline-detail.model';
import { PipelineError } from '@zerops/models/error-backend';
import { Container, ContainerEntity, ContainerWithService } from '@zerops/zerops/core/container-base';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { ProjectEntity } from '@zerops/zerops/core/project-base';
import { Actions } from '@ngrx/effects';
import { SatPopover } from '@zerops/zef/popover';
import { onWebsocketMessageDispatchAddRemoveEntities } from '@zerops/zef/entities';
import { GeneralTranslations } from '@zerops/zerops/app';
import { parseISO, isValid, isBefore, subSeconds } from 'date-fns/esm';
import { ServiceStackStatuses } from '@zerops/models/service-stack';
import { toObservable } from '@angular/core/rxjs-interop';
import { TerminalFeature } from '../terminal/terminal.feature';
import { hasProjectFullAccess } from '@zerops/models/client-user';

const CREATED_BY_TYPE_PREFIX_MAP = {
  GITHUB: 'GitHub:',
  GITLAB: 'GitLab:',
  CLI: 'zcli:',
  USER: 'Zerops:'
};

// cancelled step may still have logs, e.g. build commands cancelled mid-run or init container failure
const LOG_AVAILABLE_STATES = new Set([
  BuildProcessStates.Running,
  BuildProcessStates.Finished,
  BuildProcessStates.Failed,
  BuildProcessStates.Cancelled
]);

@Component({
  selector: 'z-pipeline-detail',
  templateUrl: './pipeline-detail.feature.html',
  styleUrls: [ './pipeline-detail.feature.scss' ]
})
export class PipelineDetailFeature extends ZefReactiveComponent {

  // # Deps
  #userEntity = inject(UserEntity);
  #projectEntity = inject(ProjectEntity);
  #serviceStackEntity = inject(ServiceStackEntity);
  #processEntity = inject(ProcessEntity);
  #containerEntity = inject(ContainerEntity);
  #actions$ = inject(Actions);

  // # Event Streams
  onOpenCopyCodeDialog$ = new Subject<void>();
  onBuildCancel$ = new Subject<string>();

  // # Data
  // -- sync
  buildProcessStates = BuildProcessStates;
  processStatuses = ProcessStatuses;
  appVersionStatuses = AppVersionStatuses;
  serviceStackStatuses = ServiceStackStatuses;
  forceShowBuildContainer = signal(false);
  forceShowRunPrepareContainer = signal(false);
  showOnlyBuildLogs = signal(true);
  activeMode = signal<PipelineDetailModes>('PIPELINE');

  // - angular
  @ObservableInput()
  @Input('appVersion')
  appVersion$!: Observable<AppVersion>;

  @ObservableInput()
  @Input('pipelineErrors')
  pipelineErrors$!: Observable<PipelineError[]>;

  @ObservableInput()
  @Input('baseKey')
  baseKey$!: Observable<string>;

  @ObservableInput()
  @Input('actionStarted')
  actionStarted$!: Observable<string>;

  mode = input<PipelineDetailModes>('PIPELINE');
  buildTerminalRef = viewChild<TerminalFeature>('buildTerminalRef');
  prepareTerminalRef = viewChild<TerminalFeature>('prepareTerminalRef');

  modeEffect = effect(() => {
    if (this.mode()) {
      this.showOnlyBuildLogs.set(true);
    }

    this.activeMode.set(this.mode())

    if (this.mode() === 'BUILD_TERMINAL') {
      setTimeout(() => {
        this.buildTerminalRef()?.focus();
      }, 200);
    }

    if (this.mode() === 'PREPARE_TERMINAL') {
      setTimeout(() => {
        this.prepareTerminalRef()?.focus();
      }, 200);
    }

  }, { allowSignalWrites: true });

  @Input()
  maxWidthDetail: string;

  @Input()
  maxWidthLogs: string;

  @Input()
  hasBackbutton = false;

  @Input()
  backbuttonRoute: any[];

  @Input()
  logsScrollHeight = 'calc(85vh - 40px)';

  @Input()
  terminalScrollHeight = 'calc(85vh - 60px)';

  @Output()
  linkClicked = new EventEmitter<void>();

  @Output()
  modeChanged = new EventEmitter<PipelineDetailModes>();

  moreOptionsRef: SatPopover;

  // -- async
  generalTranslations$ = this.translate$<GeneralTranslations>('general');
  serviceStackId$ = this.onInit$.pipe(
    tap(() => this.activeMode.set('PIPELINE')),
    switchMap(() => this.appVersion$.pipe(
      filter((d) => !!d),
      map(({ serviceStackId }) => serviceStackId),
      distinctUntilChanged()
    ))
  );
  pipelineServiceStackIds$ = this.onInit$.pipe(
    switchMap(() => this.appVersion$.pipe(
      filter((d) => !!d),
      map((d) => ({
        build: d.build?.serviceStackId,
        prepare: d.prepareCustomRuntime?.serviceStackId
      }))
    ))
  );
  serviceStack$ = this.serviceStackId$.pipe(
    switchMap((id) => this.#serviceStackEntity.entityById$(id)),
    filter((d) => !!d?.project)
  );
  #projectId$ = this.serviceStack$.pipe(
    filter((d) => !!d),
    map((d) => d?.projectId)
  );
  #project$ = this.#projectId$.pipe(
    switchMap((projectId) => this.#projectEntity.entityById$(projectId))
  );
  pipelineState$ = this.onInit$.pipe(
    switchMap(() => this.appVersion$.pipe(
      filter((d) => !!d),
      map((appVersion) => getPipelineState(appVersion))
    ))
  );
  logFailedRange$ = this.onInit$.pipe(
    switchMap(() => this.appVersion$.pipe(
      filter((d) => !!d?.build),
      map((d) => d.build.pipelineFailed),
      filter((pipelineFailed) => !!pipelineFailed),
      map((pipelineFailed) => {
        const failed = new Date(pipelineFailed);
        const beforeFailed = new Date(pipelineFailed);
        const afterFailed = new Date(pipelineFailed);
        beforeFailed.setMinutes(failed.getMinutes() - 1);
        afterFailed.setMinutes(failed.getMinutes() + 1);
        return { from: beforeFailed.toISOString(), till: afterFailed.toISOString()}
      })
    ))
  );
  emptyBaseKey$ = this.baseKey$.pipe(startWith(undefined as string));
  uniqBuildLogKey$ = this.pipelineServiceStackIds$.pipe(
    withLatestFrom(this.emptyBaseKey$),
    map(([ { build }, baseKey ]) => this._getKey(this._buildLogKey, baseKey, build))
  );
  uniqPrepareLogKey$ = this.pipelineServiceStackIds$.pipe(
    withLatestFrom(this.emptyBaseKey$),
    map(([ { prepare }, baseKey ]) => this._getKey(this._prepareLogKey, baseKey, prepare))
  );
  createdByTypePrefix$ = this.appVersion$.pipe(
    map((d) => CREATED_BY_TYPE_PREFIX_MAP[d?.createdByUser?.type])
  );
  containers$ = this.serviceStackId$.pipe(
    filter((id) => !!id),
    switchMap((id) => this.#containerEntity.listWithService$(
      { name: FEATURE_NAME, id },
      [ 'created' ],
      [ 'asc' ]
    )),
    scan((acc: ContainerWithService[], current: ContainerWithService[]) => {
      // ignore a transient drop to empty (re-subscription); otherwise take
      // the latest so status changes and removals are reflected
      if (current.length === 0 && acc.length > 0) {
        return acc;
      }
      return current;
    }, []),
  );
  buildContainerStatus$ = this.pipelineState$.pipe(
    map((d) => {
      if (d.INIT_BUILD_CONTAINER === this.buildProcessStates.Running) {
        return ServiceStackStatuses.Creating;
      }

      if (d.INIT_BUILD_CONTAINER === this.buildProcessStates.Failed) {
        return ServiceStackStatuses.Failed;
      }

      if (d.RUN_BUILD_COMMANDS === this.buildProcessStates.Finished) {
        return ServiceStackStatuses.Deleted;
      }

      if (d.RUN_BUILD_COMMANDS === this.buildProcessStates.Failed) {
        return ServiceStackStatuses.Deleted;
      }

      return ServiceStackStatuses.Active;

    })
  );
  runPrepareContainerStatus$ = this.pipelineState$.pipe(
    map((d) => {
      if (d.INIT_PREPARE_CONTAINER === this.buildProcessStates.Running) {
        return ServiceStackStatuses.Creating;
      }

      if (d.INIT_PREPARE_CONTAINER === this.buildProcessStates.Failed) {
        return ServiceStackStatuses.Failed;
      }

      if (d.RUN_PREPARE_COMMANDS === this.buildProcessStates.Finished) {
        return ServiceStackStatuses.Deleted;
      }

      if (d.DEPLOY === this.buildProcessStates.Failed) {
        return ServiceStackStatuses.Deleted;
      }

      return ServiceStackStatuses.Active;

    })
  );
  oldNewContainersMap$ = this.containers$.pipe(
    withLatestFrom(this.appVersion$, this.actionStarted$.pipe(startWith(undefined as string))),
    map(([containers, appVersion, actionStarted]) => {
      const pipelineStart = parseISO(appVersion?.build ? appVersion?.build?.pipelineStart : actionStarted);
      return containers?.reduce((acc, container) => {
        const containerCreated = parseISO(container.created);

        if (!isValid(containerCreated)) {
          return acc;
        }
        const category = isBefore(containerCreated, pipelineStart) ? 'old' : 'new';
        acc[category].push(container);

        return acc;
      }, { old: [], new: [] } as { old: Container[]; new: Container[] }) || { old: [], new: [] };
    })
  );
  processes$ = this.#processEntity.list$();
  buildProcess$ = this.appVersion$.pipe(
    switchMap(({ id }) => this.processes$.pipe(
      filter((d) => !!d),
      map((processes) => processes.find((process) => process?.appVersion?.id === id))
    ))
  );
  #logServiceStack$ = this.serviceStack$.pipe(
    filter((d) => !!d),
    map(({ projectId, instanceId }) => ({ projectId, instanceId })),
    distinctUntilChanged((prev, next) => prev.projectId === next.projectId && prev.instanceId === next.instanceId)
  );
  buildLogParams$ = combineLatest([
    this.#logServiceStack$,
    this.#logAvailableAppVersion$('RUN_BUILD_COMMANDS'),
    toObservable(this.showOnlyBuildLogs)
  ]).pipe(
    map(([ { projectId, instanceId }, appVersion, showOnlyBuildLogs ]) => appVersion
      ? {
        projectId,
        instanceId,
        serviceStackId: appVersion.build?.serviceStackId,
        page: 5000,
        from: appVersion.build?.pipelineStart
          ? subSeconds(new Date(appVersion.build.pipelineStart), 5)
          : undefined,
        tags: showOnlyBuildLogs
          ? `zbuilder@${appVersion.id}`
          : undefined
      }
      : undefined
    )
  );
  prepareLogParams$ = combineLatest([
    this.#logServiceStack$,
    this.#logAvailableAppVersion$('RUN_PREPARE_COMMANDS')
  ]).pipe(
    map(([ { projectId, instanceId }, appVersion ]) => appVersion
      ? {
        projectId,
        instanceId,
        serviceStackId: appVersion.prepareCustomRuntime?.serviceStackId,
        page: 5000
      }
      : undefined
    )
  );
  buildProcessState$ = this.pipelineState$.pipe(
    filter((d) => !!d),
    map((d) => d?.RUN_BUILD_COMMANDS)
  );
  prepareProcessState$ = this.pipelineState$.pipe(
    filter((d) => !!d),
    map((d) => d?.RUN_PREPARE_COMMANDS)
  );
  #activeClientUser$ = this.#userEntity.activeClientUser$.pipe(
    filter((d) => !!d)
  );
  #hasProjectFullAccess$ = this.#project$.pipe(
    filter((d) => !!d),
    combineLatestWith(this.#activeClientUser$),
    map(([ project, clientUser ]) => hasProjectFullAccess(project, clientUser))
  );
  #isReadOnly$ = this.#hasProjectFullAccess$.pipe(
    map((hasFullAccess) => !hasFullAccess)
  );

   // # State resolver
   state = this.$connect({
    serviceStack: this.serviceStack$,
    appVersion: this.appVersion$,
    actionStarted: this.actionStarted$,
    generalTranslations: this.generalTranslations$,
    pipelineErrors: this.pipelineErrors$,
    createdByTypePrefix: this.createdByTypePrefix$,
    pipelineState: this.pipelineState$,
    logFailedRange: this.logFailedRange$ || of({ from: '', till: '' }),
    uniqBuildLogKey: this.uniqBuildLogKey$,
    uniqPrepareLogKey: this.uniqPrepareLogKey$,
    buildProcess: this.buildProcess$,
    buildLogParams: this.buildLogParams$,
    prepareLogParams: this.prepareLogParams$,
    buildProcessState: this.buildProcessState$,
    prepareProcessState: this.prepareProcessState$,
    oldNewContainersMap: this.oldNewContainersMap$,
    buildContainerStatus: this.buildContainerStatus$,
    runPrepareContainerStatus: this.runPrepareContainerStatus$,
    isReadOnly: this.#isReadOnly$
  });

  private _buildLogKey = `${FEATURE_NAME}_BUILD`;
  private _prepareLogKey = `${FEATURE_NAME}_PREPARE`;

  private _openCopyCodeDialogAction$ = this.onOpenCopyCodeDialog$.pipe(
    withLatestFrom(this.appVersion$),
    map(([ _, appVersion ]) => copyCodeDialogOpen(appVersion?.configContent, 'Pipeline config (zerops.yml)'))
  );

  private _onBuildCancelAction$ = this.onBuildCancel$.pipe(
    map((id) => buildCancel({ id }))
  );

  private _setupContainerListStreamAction$ = this.serviceStack$.pipe(
    distinctUntilKeysChanged([ 'id' ]),
    map((serviceStack) => this.#containerEntity.listSubscribe(
      serviceStack.project.clientId,
      {
        name: FEATURE_NAME,
        id: serviceStack.id
      },
      {
        search: [
          {
            name: 'serviceStackId',
            operator: 'eq',
            value: serviceStack.id
          }
        ]
      }
    ))
  );

  private _setupAddRemoveContainerMessageAction$ = this.serviceStackId$.pipe(
    filter((id) => !!id),
    distinctUntilChanged(),
    switchMap(() => this.#actions$.pipe(
      onWebsocketMessageDispatchAddRemoveEntities(
        this.#containerEntity,
        FEATURE_NAME,
        undefined,
        this.serviceStackId$
      )
    ))
  );

  private _setupContainerUpdateStreamAction$ = this.serviceStack$.pipe(
    distinctUntilKeysChanged([ 'id' ]),
    map((serviceStack) => this.#containerEntity.updateSubscribe(
      serviceStack.project.clientId,
      {
        search: [
          {
            name: 'serviceStackId',
            operator: 'eq',
            value: serviceStack.id
          }
        ]
      }
    ))
  );

  constructor() {
    super();

    this.$dispatchActions([
      this._openCopyCodeDialogAction$,
      this._onBuildCancelAction$,
      this._setupContainerListStreamAction$,
      this._setupAddRemoveContainerMessageAction$,
      this._setupContainerUpdateStreamAction$
    ]);

  }

  /**
   * Emits the current app version once its step has logs, `undefined` while it does not,
   * re-evaluated for every app version the detail switches to.
   */
  #logAvailableAppVersion$(step: keyof BuildProcessStepState) {
    return this.onInit$.pipe(
      switchMap(() => this.appVersion$),
      filter((d) => !!d),
      map((d) => LOG_AVAILABLE_STATES.has(getPipelineState(d)[step]) ? d : undefined),
      distinctUntilChanged((prev, next) => prev?.id === next?.id)
    );
  }

  private _getKey(key: string, baseKey: string, id: string) {
    const base = `${key}_${id}`;
    if (!!baseKey) { return `${baseKey}_${base}`; }
    return base;
  }

  moreOptions(moreOptionsRef: SatPopover) {
    this.moreOptionsRef = moreOptionsRef;
  }

}
